import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AnimatedScroll from "../../components/AnimatedScroll/AnimatedScroll";
import { getImagesConfig } from "../../config/imageConfig";
import {
  fetchEventsFromApi,
  fetchGoogleReviewsFromApi,
  fetchOpeningHoursFromApi,
  resolveEventImageUrl,
} from "../../utils/contentApi";

function formatPublishedDate(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatRating(value) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return "-";
  }

  return parsed.toFixed(1).replace(".", ",");
}

function buildStarStates(value) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return ["empty", "empty", "empty", "empty", "empty"];
  }

  const safe = Math.min(Math.max(parsed, 0), 5);

  return Array.from({ length: 5 }, (_unused, index) => {
    const threshold = index + 1;
    if (safe >= threshold) {
      return "full";
    }

    if (safe >= threshold - 0.5) {
      return "half";
    }

    return "empty";
  });
}

function getAuthorInitial(value) {
  const safe = String(value || "").trim();
  return safe ? safe.charAt(0).toUpperCase() : "G";
}

function formatEventDateRange(startDate, endDate) {
  const safeStartDate = String(startDate || "").trim();
  const safeEndDate = String(endDate || "").trim();

  if (!safeStartDate && !safeEndDate) {
    return "";
  }

  const formatDate = (value) => {
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (safeStartDate && safeEndDate) {
    return `Du ${formatDate(safeStartDate)} au ${formatDate(safeEndDate)}`;
  }

  return `Jusqu'au ${formatDate(safeEndDate || safeStartDate)}`;
}

const EVENT_POPUP_APPEAR_DELAY_MS = 1400;

function formatSlots(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return "";
  return slots
    .map((s) => `${s.open.replace(":", "h")} – ${s.close.replace(":", "h")}`)
    .join(" / ");
}

function slotsKey(day) {
  if (day.isClosed) return "__closed__";
  if (!Array.isArray(day.slots) || day.slots.length === 0) return "__closed__";
  return day.slots.map((s) => `${s.open}-${s.close}`).join("|");
}

function groupConsecutiveDays(days) {
  if (!Array.isArray(days) || days.length === 0) return [];
  const groups = [];
  let current = {
    labels: [days[0].label],
    key: slotsKey(days[0]),
    day: days[0],
  };

  for (let i = 1; i < days.length; i++) {
    const k = slotsKey(days[i]);
    if (k === current.key) {
      current.labels.push(days[i].label);
    } else {
      groups.push(current);
      current = { labels: [days[i].label], key: k, day: days[i] };
    }
  }
  groups.push(current);
  return groups;
}

function Home() {
  document.title = "Carpe Diem - Accueil";

  const config = getImagesConfig();
  const [googleReviews, setGoogleReviews] = useState({
    placeName: "",
    rating: null,
    userRatingCount: 0,
    googleMapsUri: "",
    reviews: [],
    updatedAt: "",
  });
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewTransitionDirection, setReviewTransitionDirection] =
    useState("next");
  const reviewTouchStartXRef = useRef(null);
  const reviewTouchCurrentXRef = useRef(null);

  const [homeEvents, setHomeEvents] = useState([]);
  const [eventPopupOpen, setEventPopupOpen] = useState(false);
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [eventTransitionDirection, setEventTransitionDirection] =
    useState("next");
  const eventTouchStartXRef = useRef(null);
  const eventTouchCurrentXRef = useRef(null);

  const [openingHours, setOpeningHours] = useState(null);

  const reviews = useMemo(
    () =>
      Array.isArray(googleReviews?.reviews)
        ? googleReviews.reviews.filter((item) =>
            String(item?.text || "").trim(),
          )
        : [],
    [googleReviews],
  );

  const activeReview = useMemo(() => {
    if (reviews.length === 0) {
      return null;
    }

    return reviews[reviewIndex] || reviews[0];
  }, [reviewIndex, reviews]);

  const activeEvent = useMemo(() => {
    if (homeEvents.length === 0) {
      return null;
    }

    return homeEvents[activeEventIndex] || homeEvents[0];
  }, [activeEventIndex, homeEvents]);

  const activeEventTargetPath = useMemo(() => {
    if (!activeEvent) {
      return "/evenements";
    }

    if (activeEvent.category === "daily-menu") {
      const menuId = String(activeEvent.linkedMenuId || "").trim();
      return menuId ? `/menu?menuId=${encodeURIComponent(menuId)}` : "/menu";
    }

    return "/evenements";
  }, [activeEvent]);

  useEffect(() => {
    let isMounted = true;
    let popupDelayTimer = null;

    const loadEvents = async () => {
      try {
        const payload = await fetchEventsFromApi();
        if (!isMounted) {
          return;
        }

        const normalized = Array.isArray(payload)
          ? payload.filter(
              (eventItem) =>
                String(eventItem?.title || "").trim() &&
                String(eventItem?.imageUrl || "").trim(),
            )
          : [];

        setHomeEvents(normalized);

        if (normalized.length > 0) {
          popupDelayTimer = setTimeout(() => {
            if (!isMounted) {
              return;
            }

            setEventPopupOpen(true);
          }, EVENT_POPUP_APPEAR_DELAY_MS);
          return;
        }

        setEventPopupOpen(false);
      } catch (_error) {
        if (!isMounted) {
          return;
        }

        setHomeEvents([]);
        setEventPopupOpen(false);
      }
    };

    loadEvents();

    const handleContentChanged = () => loadEvents();
    window.addEventListener("carpe-content-changed", handleContentChanged);

    return () => {
      isMounted = false;
      window.removeEventListener("carpe-content-changed", handleContentChanged);
      if (popupDelayTimer) {
        clearTimeout(popupDelayTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (homeEvents.length === 0) {
      if (activeEventIndex !== 0) {
        setActiveEventIndex(0);
      }
      return;
    }

    if (activeEventIndex >= homeEvents.length) {
      setActiveEventIndex(0);
    }
  }, [activeEventIndex, homeEvents.length]);

  useEffect(() => {
    if (!eventPopupOpen || homeEvents.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setEventTransitionDirection("next");
      setActiveEventIndex((prev) => (prev + 1) % homeEvents.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [eventPopupOpen, homeEvents.length]);

  useEffect(() => {
    let isMounted = true;

    const loadGoogleReviews = async () => {
      setReviewsLoading(true);
      try {
        const payload = await fetchGoogleReviewsFromApi();
        if (!isMounted) {
          return;
        }

        setGoogleReviews((prev) => ({
          ...prev,
          ...payload,
          reviews: Array.isArray(payload?.reviews) ? payload.reviews : [],
        }));
      } catch (_error) {
        if (!isMounted) {
          return;
        }

        setGoogleReviews((prev) => ({
          ...prev,
          reviews: Array.isArray(prev.reviews) ? prev.reviews : [],
        }));
      } finally {
        if (isMounted) {
          setReviewsLoading(false);
        }
      }
    };

    loadGoogleReviews();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadOpeningHours = async () => {
      try {
        const data = await fetchOpeningHoursFromApi();
        if (isMounted && data && Array.isArray(data.days)) {
          setOpeningHours(data);
        }
      } catch (_error) {
        // keep null — fallback hardcoded will show
      }
    };
    loadOpeningHours();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (reviews.length === 0) {
      if (reviewIndex !== 0) {
        setReviewIndex(0);
      }
      return;
    }

    if (reviewIndex >= reviews.length) {
      setReviewIndex(0);
    }
  }, [reviewIndex, reviews.length]);

  useEffect(() => {
    if (reviews.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setReviewTransitionDirection("next");
      setReviewIndex((prev) => (prev + 1) % reviews.length);
    }, 9000);

    return () => clearInterval(timer);
  }, [reviews.length]);

  const goToPreviousReview = () => {
    if (reviews.length <= 1) {
      return;
    }

    setReviewTransitionDirection("prev");
    setReviewIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const goToNextReview = () => {
    if (reviews.length <= 1) {
      return;
    }

    setReviewTransitionDirection("next");
    setReviewIndex((prev) => (prev + 1) % reviews.length);
  };

  const handleReviewTouchStart = (event) => {
    if (reviews.length <= 1) {
      return;
    }

    const point = event.changedTouches?.[0];
    if (!point || typeof point.clientX !== "number") {
      return;
    }

    reviewTouchStartXRef.current = point.clientX;
    reviewTouchCurrentXRef.current = point.clientX;
  };

  const handleReviewTouchMove = (event) => {
    const point = event.changedTouches?.[0];
    if (!point || typeof point.clientX !== "number") {
      return;
    }

    reviewTouchCurrentXRef.current = point.clientX;
  };

  const handleReviewTouchEnd = () => {
    const startX = reviewTouchStartXRef.current;
    const endX = reviewTouchCurrentXRef.current;

    reviewTouchStartXRef.current = null;
    reviewTouchCurrentXRef.current = null;

    if (typeof startX !== "number" || typeof endX !== "number") {
      return;
    }

    const swipeDelta = endX - startX;
    const swipeThreshold = 45;

    if (swipeDelta <= -swipeThreshold) {
      goToNextReview();
      return;
    }

    if (swipeDelta >= swipeThreshold) {
      goToPreviousReview();
    }
  };

  const goToPreviousEvent = () => {
    if (homeEvents.length <= 1) {
      return;
    }

    setEventTransitionDirection("prev");
    setActiveEventIndex(
      (prev) => (prev - 1 + homeEvents.length) % homeEvents.length,
    );
  };

  const goToNextEvent = () => {
    if (homeEvents.length <= 1) {
      return;
    }

    setEventTransitionDirection("next");
    setActiveEventIndex((prev) => (prev + 1) % homeEvents.length);
  };

  const handleEventTouchStart = (event) => {
    if (homeEvents.length <= 1) {
      return;
    }

    const point = event.changedTouches?.[0];
    if (!point || typeof point.clientX !== "number") {
      return;
    }

    eventTouchStartXRef.current = point.clientX;
    eventTouchCurrentXRef.current = point.clientX;
  };

  const handleEventTouchMove = (event) => {
    const point = event.changedTouches?.[0];
    if (!point || typeof point.clientX !== "number") {
      return;
    }

    eventTouchCurrentXRef.current = point.clientX;
  };

  const handleEventTouchEnd = () => {
    const startX = eventTouchStartXRef.current;
    const endX = eventTouchCurrentXRef.current;

    eventTouchStartXRef.current = null;
    eventTouchCurrentXRef.current = null;

    if (typeof startX !== "number" || typeof endX !== "number") {
      return;
    }

    const swipeDelta = endX - startX;
    const swipeThreshold = 45;

    if (swipeDelta <= -swipeThreshold) {
      goToNextEvent();
      return;
    }

    if (swipeDelta >= swipeThreshold) {
      goToPreviousEvent();
    }
  };

  return (
    <>
      {eventPopupOpen && activeEvent ? (
        <div
          className="home-events-popup-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setEventPopupOpen(false)}
        >
          <article
            key={activeEvent.id || `event-${activeEventIndex}`}
            className={`home-events-popup-card home-events-popup-slide-${eventTransitionDirection}`}
            onClick={(event) => event.stopPropagation()}
            onTouchStart={handleEventTouchStart}
            onTouchMove={handleEventTouchMove}
            onTouchEnd={handleEventTouchEnd}
            onTouchCancel={handleEventTouchEnd}
          >
            <button
              type="button"
              className="home-events-popup-close"
              aria-label="Fermer la popup événements"
              onClick={() => setEventPopupOpen(false)}
            >
              ×
            </button>

            <div className="home-events-popup-media-wrap">
              <img
                className="home-events-popup-media"
                src={resolveEventImageUrl(activeEvent.imageUrl)}
                alt={activeEvent.title}
                loading="lazy"
                style={{
                  objectFit: activeEvent.imageFit || "cover",
                  objectPosition: activeEvent.imagePosition || "center",
                }}
              />
            </div>

            <div className="home-events-popup-content">
              <p className="home-events-popup-kicker">
                {activeEvent.category === "daily-menu"
                  ? "Menu du jour"
                  : "Événement"}
              </p>
              <h3>{activeEvent.title}</h3>
              {formatEventDateRange(
                activeEvent.startDate,
                activeEvent.endDate,
              ) ? (
                <p className="home-events-popup-date">
                  {formatEventDateRange(
                    activeEvent.startDate,
                    activeEvent.endDate,
                  )}
                </p>
              ) : null}
              {activeEvent.description ? (
                <p className="home-events-popup-description">
                  {activeEvent.description}
                </p>
              ) : null}

              <div className="home-events-popup-actions">
                <button
                  type="button"
                  className="home-events-popup-arrow"
                  onClick={goToPreviousEvent}
                  disabled={homeEvents.length <= 1}
                  aria-label="Événement précédent"
                >
                  {"<"}
                </button>

                <Link
                  className="home-events-popup-link"
                  to={activeEventTargetPath}
                  onClick={() => setEventPopupOpen(false)}
                >
                  Savoir plus
                </Link>

                <button
                  type="button"
                  className="home-events-popup-arrow"
                  onClick={goToNextEvent}
                  disabled={homeEvents.length <= 1}
                  aria-label="Événement suivant"
                >
                  {">"}
                </button>
              </div>

              {homeEvents.length > 1 ? (
                <div
                  className="home-events-popup-dots"
                  aria-label="Navigation événements"
                >
                  {homeEvents.map((eventItem, index) => (
                    <button
                      key={eventItem.id || index}
                      type="button"
                      className={`home-events-popup-dot${
                        activeEventIndex === index ? " active" : ""
                      }`}
                      onClick={() => {
                        if (index === activeEventIndex) {
                          return;
                        }

                        setEventTransitionDirection(
                          index > activeEventIndex ? "next" : "prev",
                        );
                        setActiveEventIndex(index);
                      }}
                      aria-label={`Afficher l'événement ${index + 1}`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </article>
        </div>
      ) : null}

      <AnimatedScroll>
        <section id="welcome-hero" className="accueil">
          <div className="section1">
            <div className="component1"></div>
            <div className="component1"></div>
            <div className="contactHome">
              <div className="contactContent">
                <div className="contactFeature">
                  {/* adresse*/}
                  <div className="item1Contact">
                    <div className="IconWrapper">
                      <div className="CircleIcon">
                        <div className="styled_icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="24"
                            viewBox="0 0 24 24"
                            width="24"
                          >
                            <path d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h2 className="titleContact">Carpe Diem Goutrens</h2>
                    <p className="textContact">
                      242 Route de Goutrens
                      <br />
                      12390 Goutrens
                    </p>
                  </div>
                  {/* contact*/}
                  <div className="item1Contact">
                    <div className="IconWrapper">
                      <div className="CircleIcon">
                        <div className="styled_icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                          >
                            <path d="M28.424 10.667H1.432l13.501 10.382zM15.584 23.233a1.065 1.065 0 01-1.3 0l-1.656-1.27-11.215 7.893h27.043l-11.215-7.893-1.656 1.27zM19.007 20.602l10.85 7.639v-15.99zM10.86 20.602L0 28.241v-15.99zM28.79 2.133H18.123a3.206 3.206 0 00-3.21 3.19 3.2 3.2 0 106.217-1.057h4.653a3.2 3.2 0 103.007-2.133z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <a
                      className="titleContact emailContact linkScale"
                      href="mailto:contact@carpediem12.com"
                    >
                      contact@carpediem12.com
                    </a>
                    <p className="textContact phoneContact">
                      <span
                        className="phoneIcon"
                        aria-label="téléphone"
                        role="img"
                      ></span>
                      <a
                        className="phoneLink linkScale"
                        href="tel:0563714445"
                        style={{
                          color: "inherit",
                          fontWeight: "bold",
                          fontSize: "1.2em",
                          textDecoration: "none",
                        }}
                      >
                        {/* Icône téléphone blanche */}
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="white"
                          style={{
                            marginRight: "8px",
                            verticalAlign: "middle",
                          }}
                        >
                          <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z" />
                        </svg>
                        05 63 71 44 45
                      </a>
                    </p>
                  </div>
                  {/* horaires */}
                  <div className="item1Contact">
                    <div className="IconWrapper">
                      <div className="CircleIcon">
                        <div className="styled_icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 32 32"
                          >
                            <path d="M16 4c6.627 0 12 5.373 12 12s-5.373 12-12 12S4 22.627 4 16 9.373 4 16 4zm0 3.429c-.757 0-1.371.614-1.371 1.371v7.371a1.8 1.8 0 00.531 1.269l3.429 3.343a1.371 1.371 0 001.937 0 1.354 1.354 0 000-1.938l-3.154-3.069V8.799c0-.757-.614-1.371-1.371-1.371z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h2 className="titleContact">Horaires</h2>
                    <p className="textContact">
                      {openingHours && Array.isArray(openingHours.days) ? (
                        groupConsecutiveDays(openingHours.days).map(
                          (group, idx) => {
                            const label =
                              group.labels.length > 1
                                ? `${group.labels[0]} à ${group.labels[group.labels.length - 1]}`
                                : group.labels[0];
                            return (
                              <span key={idx}>
                                <strong>{label}</strong>
                                <br />
                                {group.day.isClosed
                                  ? "Fermé"
                                  : formatSlots(group.day.slots)}
                                <br />
                              </span>
                            );
                          },
                        )
                      ) : (
                        <>
                          <strong>Lundi à Jeudi</strong> <br />
                          09h00 – 15h00
                          <br />
                          <strong>Vendredi</strong>
                          <br />
                          09h00 – 15h00 / 18h00 – 00h00 <br />
                          <strong>Samedi</strong>
                          <br /> Fermé <br /> <strong>Dimanche</strong> <br />
                          10h00 – 16h00
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedScroll>
      <AnimatedScroll>
        <section className="accueil">
          <div className="=section1">
            <div className="component1"></div>
            <div className="component1"></div>
            <div className="reserveContent">
              <div className="reserveFeature reserveFeature2">
                <div className="reserveText">
                  <div className="textReserve">
                    <h1
                      data-text="Exceptional taste &amp;
warm atmosphere."
                      className="h1Reserve"
                    >
                      Goût exceptionnel et ambiance chaleureuse.
                    </h1>
                    <ul className="listReserve">
                      <li className="liReserve">
                        <div className="iconLi">
                          <div
                            color="#5E503F"
                            className="logoLi"
                            data-name="glyph-house-13-complexity-3"
                          >
                            <svg
                              className="svgLi"
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M22.227 11.317h-.02a1.708 1.708 0 10.021 0h-.001z"></path>
                              <path d="M16.366 3.302v24.157H19.8V19.22c0-.191.155-.345.345-.345h4.124c.185 0 .335.15.335.335v8.239h3.769V10.301zm5.831 12.139h-.01a2.408 2.408 0 112.408-2.419v.011a2.408 2.408 0 01-2.397 2.408h-.001zM3.627 16.244v11.215h12.048V9.255zm8.614 7.101a.345.345 0 01-.345.345H7.782a.345.345 0 01-.345-.345v-4.124c0-.191.155-.345.345-.345h4.114c.191 0 .345.155.345.345z"></path>
                              <path d="M8.117 19.566h3.434V23H8.117v-3.434zM15.675 7.446L2.459 15.004a.987.987 0 00-.507.895v-.002.681l13.735-7.853v-1.28zM14.324 1.524l15.766 9.194v-.67a1.064 1.064 0 00-.503-.891l-.005-.003L15.563.966a.904.904 0 00-.419-.102H15.126a.83.83 0 00-.589.244.777.777 0 00-.213.412l-.001.005zM31.756 26.484a7.061 7.061 0 00-1.391.32l.05-.015c.405-.281.874-.561 1.361-.81l.071-.033a.213.213 0 00-.195-.375h.002c-.665.342-1.23.693-1.765 1.083l.038-.026c.285-.411.596-.771.941-1.094l.004-.003a.233.233 0 00.01-.305.214.214 0 00-.294 0 6.025 6.025 0 00-.812.888l-.011.016c.356-1.148.782-1.636.792-1.636a.224.224 0 00-.01-.305.216.216 0 00-.295.01 3.31 3.31 0 00-.479.742l-.009.02c-.254.508-.711 1.9-.711 2.509h.914c.386-.315 1.443-.508 1.829-.559a.244.244 0 00.193-.238v-.006a.203.203 0 00-.234-.182h.001zM2.936 27.347c-.183-1.128-.467-1.91-.721-2.418a3.327 3.327 0 00-.491-.766l.004.005a.215.215 0 00-.295-.01.223.223 0 00-.01.305c.01 0 .437.477.792 1.636a7.385 7.385 0 00-.819-.9l-.004-.004a.214.214 0 00-.294 0 .244.244 0 00.01.305c.354.32.666.677.932 1.067l.013.02a14.084 14.084 0 00-.63-.437 16.982 16.982 0 00-1.087-.62.213.213 0 00-.284.09l-.001.001a.214.214 0 00.09.284l.001.001c.458.228.837.447 1.202.687l-.043-.027.264.183a7.582 7.582 0 00-1.298-.3l-.043-.005a.203.203 0 00-.233.201v.004c0 .101.074.186.172.201h.001c.381.055.707.122 1.027.207l-.062-.014c.356.091.711.224.894.386h.894c.03-.02.02-.081.02-.081zM7.771 29.501a.264.264 0 00-.264-.264h-2.58a.264.264 0 100 .528h2.58a.264.264 0 00.264-.264zM27.154 29.247h-7.751a.264.264 0 100 .528h7.751a.264.264 0 100-.528zM9.316 29.775h8.3a.264.264 0 100-.528h-8.3a.264.264 0 100 .528zM13.135 30.629H6.146a.264.264 0 100 .528h6.989a.264.264 0 00.264-.264v-.01a.254.254 0 00-.254-.254h-.011.001zM25.752 30.629H15.41a.264.264 0 100 .528h10.342a.264.264 0 100-.528zM20.49 19.566v7.893h3.434v-7.893zm2.387 4.124a.356.356 0 11.356-.356.356.356 0 01-.356.356z"></path>
                            </svg>
                          </div>
                        </div>
                        <p className="textLi">
                          Carpe diem a ouvert ses portes il y a peu mais c'est
                          devenu un lieu de prédilection pour les habitants et
                          les touristes qui souhaitent déguster les plats les
                          plus appétissants de la ville.
                        </p>
                      </li>
                      <li className="liReserve">
                        <div className="iconLi">
                          <div
                            color="#5E503F"
                            className="logoLi"
                            data-name="glyph-room-service-complexity-3"
                          >
                            <svg
                              className="svgLi"
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M29.796 13.237H2.205a.57.57 0 00-.549.569c.003.07.018.136.042.197l-.001-.004c.75 1.837 2.522 3.108 4.592 3.109h19.434a4.96 4.96 0 004.57-3.076l.012-.033c.295-.752-.325-.762-.508-.762zM16.549 1.666v-.559h.559a.56.56 0 00.549-.559.55.55 0 00-.549-.549h-2.215a.55.55 0 00-.549.549c0 .305.244.553.548.559h.559v.559C9.54 1.951 4.755 6.418 3.979 12.158l-.007.063h24.056c-.784-5.803-5.569-10.27-11.452-10.554l-.028-.001zM32 20.368a1.06 1.06 0 00-.084-.301l.003.007c-.325-.762-1.29-.996-1.961-.508-1.321.955-3.81 2.641-5.902 3.332-3.891 1.3-7.456 1.097-10.057.945l-.681-.041c-2.855-.142-3.525 1.554-3.535 1.564a.498.498 0 01-.467.335.552.552 0 01-.187-.032l.004.001a.508.508 0 01-.293-.664l-.001.003c.03-.102.945-2.408 4.531-2.215l.691.03c1.514.091 3.363.193 5.404 0a1.619 1.619 0 00-.247-.523l.003.005c-1.219-1.808-2.784-2.448-5.983-2.448-1.25 0-1.717-.234-2.255-.498s-1.229-.599-2.702-.599h-.096a9.982 9.982 0 00-3.921.797l.065-.025v10.809h10.017c3.058 0 8.015-1.341 9.265-2.154 7.619-4.947 8.483-6.776 8.391-7.822zM3.312 19.972v-.66a.55.55 0 00-.549-.549H.548a.55.55 0 00-.549.549v12.14a.55.55 0 00.549.549h2.215a.55.55 0 00.549-.549V19.973z"></path>
                            </svg>
                          </div>
                        </div>
                        <p className="textLi">
                          Situé au cœur de Goutrens, Carpe Diem est un
                          restaurant accueillant pour les amateurs de
                          délicatesse et d'élégance. Vous y serez accueilli dans
                          une ambiance chaleureuse et un service impeccable.
                        </p>
                      </li>
                      <li className="liReserve">
                        <div className="iconLi">
                          <div
                            color="#5E503F"
                            className="logoLi"
                            data-name="glyph-christmas--wine-complexity-3"
                          >
                            <svg
                              className="svgLi"
                              xmlns="http://www.w3.org/2000/svg"
                              width="25"
                              height="32"
                              viewBox="0 0 25 32"
                            >
                              <path d="M2.621 23.111c.736.479 1.62.794 2.57.872l.02.001v7.05H2.712a.488.488 0 00-.477.488c0 .264.214.477.477.477h5.963a.488.488 0 00.488-.477v-.001a.51.51 0 00-.487-.488h-2.5v-7.05a5.61 5.61 0 002.612-.887l-.022.013c1.625-1.026 2.601-2.855 2.621-4.785H-.001c.02 1.93.996 3.759 2.621 4.785zM10.758 12.952a.478.478 0 00-.474-.417H1.103a.477.477 0 00-.473.414v.002L.071 17.36h11.246zM21.831.477A.477.477 0 0021.354 0h-3.596a.477.477 0 00-.477.477v2.814h4.551zM18.083 15.787v12.282a.448.448 0 00.447.437h6.654V15.34H18.53c-.247 0-.447.2-.447.447z"></path>
                              <path d="M16.975 28.495V15.35c0-.617.5-1.117 1.117-1.117h7.06a5.67 5.67 0 00-3.287-4.608l-.035-.014V4.257h-4.551v5.354c-1.988.904-3.348 2.867-3.363 5.149v15.616c0 .898.728 1.625 1.625 1.625h8.025a1.625 1.625 0 001.615-1.625v-.752H18.09a1.128 1.128 0 01-1.117-1.128z"></path>
                            </svg>
                          </div>
                        </div>
                        <p className="textLi">
                          Menu exquis. Nous nous engageons à vous offrir un goût
                          exceptionnel, un confort optimal et une cuisine
                          raffinée.
                        </p>
                      </li>
                    </ul>
                    <div className="buttonReserve">
                      <div className="singleButtonReserve">
                        <div className="linkWrapperReserve ">
                          <Link
                            className="buttonLinkReserve"
                            to="/notre-restaurant#reservation-restaurant"
                          >
                            Réserver une table
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="reserveImage">
                  <div className="figureImage">
                    <div className="LazyLoad is-visible">
                      <figure className="lazy-figure figureReserve">
                        <img
                          alt={config.home?.heroImage1Alt || "Section img"}
                          src={
                            config.home?.heroImage1 ||
                            "https://i.ibb.co/2YH84nZ1/bardeco.jpg"
                          }
                          className="imgReserve img-loaded img1"
                        ></img>
                      </figure>
                    </div>
                    <div className="imageOverlay"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedScroll>
      <AnimatedScroll>
        <section>
          <div className="styled__Wrapper">
            <div className="comp"></div>
            <div className="comp"></div>
            <div className="Featurestyled StyledContainer">
              <div className="WMContainer">
                <div className="Headings-sc">
                  <h1 className="Title-sc Headings-title">
                    Ce que nous offrons
                  </h1>
                  <p className="Paragraph-sc Headings-paragraph">
                    Services de haute qualité et large gamme de produits.
                  </p>
                </div>
                <div className="BlocksContainer-sc">
                  <div className="ControlContainer-sc">
                    <div className="Block-sc">
                      <div className="IconWrap-sc">
                        <div className="Circle-sc">
                          <div
                            data-name="glyph-looks_one"
                            className="SvgIcon-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M18.688 22.688V9.313h-5.375v2.688h2.688v10.688h2.688zM25.313 4c1.438 0 2.688 1.25 2.688 2.688v18.625c0 1.438-1.25 2.688-2.688 2.688H6.688C5.25 28.001 4 26.751 4 25.313V6.688C4 5.25 5.25 4 6.688 4h18.625z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <h2 className="BlockTitle-sc Headings-blockTitle">
                        Sièges au Bar &amp; Salle à manger &amp; Terrasse
                      </h2>
                      <p className="BlockParagraph-sc Headings-blockParagraph">
                        À l'exception des sièges au bar magnifiquement conçus,
                        nous vous proposons une salle à manger confortable et
                        élégante.
                      </p>
                      <div className="AnchorContainer-sc">
                        <div className="link-wrapper ">
                          <Link
                            className="learn-more-link link"
                            to="/notre-restaurant#reservation-restaurant"
                          >
                            Réserver une table
                            <div
                              className="Icon-sc arrow-right"
                              data-name="keyboard_arrow_right"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                              >
                                <path d="M11.438 21.813l6.125-6.125-6.125-6.125 1.875-1.875 8 8-8 8z"></path>
                              </svg>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ControlContainer-sc">
                    <div className="Block-sc">
                      <div className="IconWrap-sc">
                        <div className="Circle-sc">
                          <div
                            data-name="glyph-looks_one"
                            className="SvgIcon-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M20 14.688V12c0-1.5-1.25-2.688-2.688-2.688h-5.313V12h5.313v2.688h-2.625c-1.438 0-2.688 1.125-2.688 2.625v5.375h8V20h-5.313v-2.688h2.625c1.438 0 2.688-1.125 2.688-2.625zM25.313 4c1.438 0 2.688 1.25 2.688 2.688v18.625c0 1.438-1.25 2.688-2.688 2.688H6.688C5.25 28.001 4 26.751 4 25.313V6.688C4 5.25 5.25 4 6.688 4h18.625z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <h2 className="BlockTitle-sc Headings-blockTitle">
                        Menu diversifié, exquis et personnalisé
                      </h2>
                      <p className="BlockParagraph-sc Headings-blockParagraph">
                        Notre menu est rempli de plats variés et personnalisés.
                        Notre équipe l'a conçu avec amour.
                      </p>
                      <div className="AnchorContainer-sc">
                        <div className="link-wrapper ">
                          <Link className="learn-more-link link" to="/menu">
                            Voir le menu
                            <div
                              className="Icon-sc arrow-right"
                              data-name="keyboard_arrow_right"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                              >
                                <path d="M11.438 21.813l6.125-6.125-6.125-6.125 1.875-1.875 8 8-8 8z"></path>
                              </svg>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="ControlContainer-sc">
                    <div className="Block-sc">
                      <div className="IconWrap-sc">
                        <div className="Circle-sc">
                          <div
                            data-name="glyph-looks_one"
                            className="SvgIcon-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M20 14v-2c0-1.5-1.188-2.688-2.625-2.688H12V12h5.375v2.688h-2.688v2.625h2.688v2.688H12v2.688h5.375c1.438 0 2.625-1.188 2.625-2.688v-2c0-1.125-.875-2-2-2 1.125 0 2-.875 2-2zm5.375-10C26.813 4 28 5.25 28 6.688v18.625c0 1.438-1.188 2.688-2.625 2.688H6.687c-1.438 0-2.688-1.25-2.688-2.688V6.688C3.999 5.25 5.249 4 6.687 4h18.688z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <h2 className="BlockTitle-sc Headings-blockTitle">
                        Accueil et ambiance chaleureuse
                      </h2>
                      <p className="BlockParagraph-sc Headings-blockParagraph">
                        Nous l'avons rendu confortable, esthétique, amical et
                        chaleureux pour vous et votre famille.
                      </p>
                      <div className="AnchorContainer-sc">
                        <div className="link-wrapper ">
                          <Link
                            className="learn-more-link link"
                            to="/notre-restaurant"
                          >
                            Voir notre restaurant
                            <div
                              className="Icon-sc arrow-right"
                              data-name="keyboard_arrow_right"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 32 32"
                              >
                                <path d="M11.438 21.813l6.125-6.125-6.125-6.125 1.875-1.875 8 8-8 8z"></path>
                              </svg>
                            </div>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedScroll>
      <AnimatedScroll>
        <section>
          <div className="Wrapper-sc">
            <div id="comp-1761e2350fd" className="comp"></div>
            <div id="comp-4" className="comp"></div>
            <div className="StyledContainer-sc Featurestyled-sc">
              <div className="CustomContainer-sc WMContainer-sc">
                <div className="BlocksWrapper-sc">
                  <div className="BlockStats-sc">
                    <div className="BlockItemWrap-sc">
                      <div className="IconContainer-sc">
                        <div className="IconWrapStats-sc">
                          <div
                            data-name="glyph-stars"
                            className="SvgIconStats-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M21.625 24l-1.5-6.438 5-4.313-6.563-.563-2.563-6-2.563 6.063-6.563.5 5 4.313-1.5 6.438 5.625-3.375zM16 2.688c7.375 0 13.313 5.938 13.313 13.313S23.375 29.314 16 29.314 2.687 23.376 2.687 16.001 8.625 2.688 16 2.688z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="BlockWrap-sc">
                        <h2 className="BlockTitleStats-sc TitleStats">
                          Juillet 2025
                        </h2>
                        <p className="BlockParagraphStats-sc ParagraphStats">
                          Ouvert depuis
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="BlockStats-sc">
                    <div className="BlockItemWrap-sc">
                      <div className="IconContainer-sc">
                        <div className="IconWrapStats-sc">
                          <div
                            data-name="glyph-restaurant"
                            className="SvgIconStats-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M21.313 8c0-2.375 3-5.313 6.688-5.313v26.625h-3.313V18.687h-3.375V7.999zm-6.625 4V2.687h2.625V12c0 2.813-2.188 5.125-5 5.313v12H9v-12c-2.813-.188-5-2.5-5-5.313V2.687h2.688V12h2.625V2.687h2.688V12h2.688z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="BlockWrap-sc">
                        <h2 className="BlockTitleStats-sc TitleStats">100+</h2>
                        <p className="BlockParagraphStats-sc ParagraphStats">
                          Repas
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="BlockStats-sc">
                    <div className="BlockItemWrap-sc">
                      <div className="IconContainer-sc">
                        <div className="IconWrapStats-sc">
                          <div
                            data-name="glyph-room-service-complexity-3"
                            className="SvgIconStats-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M29.796 13.237H2.205a.57.57 0 00-.549.569c.003.07.018.136.042.197l-.001-.004c.75 1.837 2.522 3.108 4.592 3.109h19.434a4.96 4.96 0 004.57-3.076l.012-.033c.295-.752-.325-.762-.508-.762zM16.549 1.666v-.559h.559a.56.56 0 00.549-.559.55.55 0 00-.549-.549h-2.215a.55.55 0 00-.549.549c0 .305.244.553.548.559h.559v.559C9.54 1.951 4.755 6.418 3.979 12.158l-.007.063h24.056c-.784-5.803-5.569-10.27-11.452-10.554l-.028-.001zM32 20.368a1.06 1.06 0 00-.084-.301l.003.007c-.325-.762-1.29-.996-1.961-.508-1.321.955-3.81 2.641-5.902 3.332-3.891 1.3-7.456 1.097-10.057.945l-.681-.041c-2.855-.142-3.525 1.554-3.535 1.564a.498.498 0 01-.467.335.552.552 0 01-.187-.032l.004.001a.508.508 0 01-.293-.664l-.001.003c.03-.102.945-2.408 4.531-2.215l.691.03c1.514.091 3.363.193 5.404 0a1.619 1.619 0 00-.247-.523l.003.005c-1.219-1.808-2.784-2.448-5.983-2.448-1.25 0-1.717-.234-2.255-.498s-1.229-.599-2.702-.599h-.096a9.982 9.982 0 00-3.921.797l.065-.025v10.809h10.017c3.058 0 8.015-1.341 9.265-2.154 7.619-4.947 8.483-6.776 8.391-7.822zM3.312 19.972v-.66a.55.55 0 00-.549-.549H.548a.55.55 0 00-.549.549v12.14a.55.55 0 00.549.549h2.215a.55.55 0 00.549-.549V19.973z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="BlockWrap-sc">
                        <h2 className="BlockTitleStats-sc TitleStats">2+</h2>
                        <p className="BlockParagraphStats-sc ParagraphStats">
                          Employés
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="BlockStats-sc">
                    <div className="BlockItemWrap-sc">
                      <div className="IconContainer-sc">
                        <div className="IconWrapStats-sc">
                          <div
                            data-name="glyph-person"
                            className="SvgIconStats-sc"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M16 18.688c3.563 0 10.688 1.75 10.688 5.313v2.688H5.313v-2.688c0-3.563 7.125-5.313 10.688-5.313zM16 16c-2.938 0-5.313-2.375-5.313-5.313S13.062 5.312 16 5.312s5.313 2.438 5.313 5.375S18.938 16 16 16z"></path>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="BlockWrap-sc">
                        <h2 className="BlockTitleStats-sc TitleStats">100+</h2>
                        <p className="BlockParagraphStats-sc ParagraphStats">
                          Clients
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedScroll>

      <AnimatedScroll>
        <section className="google-reviews-section">
          <div className="google-reviews-shell WMContainer-sc">
            <div className="google-reviews-head">
              <div>
                <div className="google-reviews-brand-left">
                  <img
                    className="google-reviews-brand-logo"
                    src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png"
                    alt="Google"
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="google-reviews-kicker">Avis Google</p>
                </div>
                <h2 className="google-reviews-title">
                  {googleReviews.placeName || "Carpe Diem Goutrens"}
                </h2>
              </div>

              <div className="google-reviews-score-wrap">
                <p className="google-reviews-provider">Google</p>
                <p className="google-reviews-score">
                  {formatRating(googleReviews.rating)}
                  <span>/5</span>
                </p>
                <div
                  className="google-reviews-global-stars"
                  aria-label={`Note Google ${formatRating(googleReviews.rating)} sur 5`}
                >
                  {buildStarStates(googleReviews.rating).map(
                    (starState, index) => (
                      <span
                        key={`global-star-${index}`}
                        className={`google-star ${starState}`}
                        aria-hidden="true"
                      >
                        ★
                      </span>
                    ),
                  )}
                </div>
                <p className="google-reviews-count">
                  {Number(googleReviews.userRatingCount || 0) > 0
                    ? `${googleReviews.userRatingCount} avis`
                    : "Note en cours de synchronisation"}
                </p>
                {googleReviews.googleMapsUri ? (
                  <a
                    className="google-reviews-link"
                    href={googleReviews.googleMapsUri}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir tous les avis
                  </a>
                ) : null}
              </div>
            </div>

            <div className="google-reviews-carousel">
              <button
                type="button"
                className="google-reviews-arrow"
                onClick={goToPreviousReview}
                disabled={reviews.length <= 1}
                aria-label="Avis precedent"
              >
                {"<"}
              </button>

              <article
                key={
                  reviewsLoading
                    ? "loading"
                    : activeReview?.id || `review-${reviewIndex}`
                }
                className={`google-review-card google-review-card-slide-${reviewTransitionDirection}`}
                onTouchStart={handleReviewTouchStart}
                onTouchMove={handleReviewTouchMove}
                onTouchEnd={handleReviewTouchEnd}
                onTouchCancel={handleReviewTouchEnd}
              >
                {reviewsLoading ? (
                  <p className="google-review-placeholder">
                    Chargement des avis Google...
                  </p>
                ) : activeReview ? (
                  <>
                    <p className="google-review-text">"{activeReview.text}"</p>
                    <div className="google-review-meta">
                      <div className="google-review-author-wrap">
                        {activeReview.authorAvatar ? (
                          <img
                            className="google-review-avatar"
                            src={activeReview.authorAvatar}
                            alt={activeReview.authorName || "Client Google"}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <span
                            className="google-review-avatar-fallback"
                            aria-hidden="true"
                          >
                            {getAuthorInitial(activeReview.authorName)}
                          </span>
                        )}

                        {activeReview.authorUrl ? (
                          <a
                            className="google-review-author-link"
                            href={activeReview.authorUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="google-review-author">
                              {activeReview.authorName || "Client Google"}
                            </span>
                          </a>
                        ) : (
                          <span className="google-review-author">
                            {activeReview.authorName || "Client Google"}
                          </span>
                        )}
                      </div>

                      {activeReview.rating !== null &&
                      activeReview.rating !== undefined ? (
                        <span className="google-review-rating">
                          <span
                            className="google-review-stars"
                            aria-hidden="true"
                          >
                            {buildStarStates(activeReview.rating).map(
                              (starState, index) => (
                                <span
                                  key={`review-star-${index}`}
                                  className={`google-star ${starState}`}
                                >
                                  ★
                                </span>
                              ),
                            )}
                          </span>
                          <span>{formatRating(activeReview.rating)}/5</span>
                        </span>
                      ) : null}
                      {formatPublishedDate(activeReview.publishedAt) ? (
                        <span className="google-review-date">
                          {formatPublishedDate(activeReview.publishedAt)}
                        </span>
                      ) : activeReview.relativeDate ? (
                        <span className="google-review-date">
                          {activeReview.relativeDate}
                        </span>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="google-review-placeholder">
                    Les avis Google seront affiches ici une fois synchronises.
                  </p>
                )}
              </article>

              <button
                type="button"
                className="google-reviews-arrow"
                onClick={goToNextReview}
                disabled={reviews.length <= 1}
                aria-label="Avis suivant"
              >
                {">"}
              </button>
            </div>

            {reviews.length > 1 && (
              <div
                className="google-reviews-dots"
                role="tablist"
                aria-label="Navigation avis"
              >
                {reviews.map((reviewItem, index) => (
                  <button
                    key={reviewItem.id || index}
                    type="button"
                    className={`google-reviews-dot${
                      reviewIndex === index ? " active" : ""
                    }`}
                    onClick={() => {
                      if (index === reviewIndex) {
                        return;
                      }

                      setReviewTransitionDirection(
                        index > reviewIndex ? "next" : "prev",
                      );
                      setReviewIndex(index);
                    }}
                    aria-label={`Afficher l'avis ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </AnimatedScroll>

      <section>
        <div className="Wrapper-sc">
          <div id="comp-1760492ded8" className="comp"></div>
          <div id="comp-5" className="comp"></div>
          <div
            className="Special-StyledContainer-sc Featurestyled-sc"
            style={{
              backgroundImage: config.home?.menuSectionBg
                ? `linear-gradient(rgba(254, 252, 252, 0), rgba(254, 252, 252, 0)), url(${config.home.menuSectionBg})`
                : undefined,
            }}
          >
            <AnimatedScroll>
              <div className="Special-CustomContainer-sc WMContainer">
                <div className="TextCompWrap-sc">
                  <div className="ContentWrap-sc">
                    <p className="Description-sc Descri">MENU</p>
                    <h1 className="Special-Title-sc Special-Title">
                      Découvrez nos menus exclusif a des prix exclusifs !
                    </h1>
                    <div className="LinkSection-sc">
                      <div className="link-wrapper ">
                        <Link
                          className="learn-more-link link WMLink"
                          to="/menu"
                        >
                          Voir Le menu
                          <div
                            className="arrow-right Icon-sc Special-Icon"
                            data-name="keyboard_arrow_right"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                            >
                              <path d="M11.438 21.813l6.125-6.125-6.125-6.125 1.875-1.875 8 8-8 8z"></path>
                            </svg>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedScroll>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
